import _ from "lodash";
import React from "react";
import classNames from "classnames";
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";
import { TickCircleFilled, InformationOutlined } from "@toddle-edu/ds-icons";

import classes from "./RubricTableWithComment.scss";
import { useI18n } from "Hooks";
import CriteriaRating from "UIComponents/CriteriaRating";
import LinkWithTooltip from "UIComponents/LinkWithTooltip";
import { getStyleStrippedText, onEnterOrSpaceKeyDown } from "Utils";
import { getCriteriaSetOfPypType } from "modules/Services";
import { updateField } from "modules/FieldUpdateThunks";
import { writeAssessmentToolElementLocal } from "modules/AssessmentTool/AssessmentToolModule";
import {
  createPypElementRating,
  deletePypElementRating,
} from "AssessmentEvaluation/modules/AssessmentEvaluationModule";
import { getUserType } from "modules/CommonSelectors";
import CriteriaComment from "Projects/components/CriteriaComment";
import { Tooltip, useDSContext } from "@toddle-edu/ds-web";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import { getIsCurriculumACSEnabled } from "Projects/config/constants";
import { getProjectCriteriaGradeCriteriaSetWithCurriculum } from "Projects/modules/Utils";

const TEST_ID_PREFIX = "projects-rubricTableWithComment";

const styles = {
  tooltipContainerStyle: {
    width: "500px",
  },
};

const RubricTableWithComment = props => {
  const { mode, isEvaluation, rubricTableContainerStyle = {} } = props;

  const { dsVersion } = useDSContext();
  const isDSV2 = dsVersion === "2.0";

  const { t } = useI18n(["unitPlan", "classRoom"]);
  const isCurriculumACSEnabled = getIsCurriculumACSEnabled();
  const getHeaderRow = () => {
    const { achievementLevels, rubricHeaderRowCellStyle = {} } = props;

    return (
      <thead>
        <tr>
          <th
            className={classNames(classes.headerRowColumnCell, {
              [classes.headerRowColumnCellV2]: isDSV2,
            })}
            aria-label="Objectives"
          ></th>
          {_.map(achievementLevels, level => {
            return (
              <th
                key={level.id}
                className={classNames({
                  [classes.headerRowCell]: true,
                  "text-label-3": !isDSV2,
                  "text-label": isDSV2,
                  [classes.headerRowCellV2]: isDSV2,
                })}
                style={rubricHeaderRowCellStyle}
              >
                {level.label}
              </th>
            );
          })}
        </tr>
      </thead>
    );
  };

  const getTableBody = () => {
    const {
      isEvaluation,
      isEvaluationEnable,
      isEvaluated,
      userType,
      seggregatedObjectives,
      parentId,
      projectId,
      parentType,
      studentId,
      studentIds = [],
      ratingElementSubType,
      ibPYPElementRatings,
      createPypElementRating,
      deletePypElementRating,
      getCriteriaSetOfPypType,
      showInsight,
      rubricRatingContainerStyle,
      rubricRatingTextContainerStyle,
      rubricRatingCompContainerStyle,
      showRatingContainerInViewMode,
      subjectLabelStyle,
      objectiveNameStyle = {},
      ibPYPElementRatingsLocal,
      setIBPYPElementRatingsLocal,
      academicTermId,
      extraParams: { sourceFrom = "", queryVars = {}, projectGroupType } = {},
      criteriaRatingElementsContainerStyle,
      criteriaRatingCustomRatingElementClass,
      showStrandComment = true,
      ibPYPElementRemarks,
      onUpdateCriteriaComment,
      commentInputRef,
      activeComment,
      showEmptyValueInSelection,
      isSelectedClickDisabled,
      getProjectCriteriaGradeCriteriaSetWithCurriculum,
    } = props;

    // currently we are picking the first criteria set as there is no distinguishing key
    // to filter out the appropriate criteria set. Later when such key is implemented then
    // we need to change this picking logic

    const criteriaSet =
      projectId && isCurriculumACSEnabled
        ? _.cloneDeep(
            getProjectCriteriaGradeCriteriaSetWithCurriculum({
              type: "IB_DEFINED",
            })
          )
        : _.cloneDeep(
            _.find(
              getCriteriaSetOfPypType({
                type: "MYP_OBJECTIVES",
                subType: ratingElementSubType,
              }),
              ({ criteriaType }) => {
                return criteriaType == "IB_DEFINED";
              }
            )
          );
    if (showEmptyValueInSelection) {
      criteriaSet?.academicCriteriaValues?.unshift({
        __typename: "AcademicCriteriaValue",
        id: "null",
        label: "-",
        abbreviation: "-",
        description: "",
        color: null,
        localGrades: [],
        scoreValues: [],
      });
    }

    // styles
    const objectiveNameClass = classNames(
      { [classes.objectiveName]: true },
      { "heading-6": !isDSV2 },
      { "text-label": isDSV2 },
      { [classes.objectiveNameSpacing]: showInsight },
      { [classes.objectiveNameV2]: isDSV2 }
    );

    const academicCriteriaValues = _.get(
      criteriaSet,
      "academicCriteriaValues",
      []
    );

    const criteriaSetId = _.get(criteriaSet, "id", "");

    const seggregatedObjectivesValues = Object.values(seggregatedObjectives);

    return _.map(
      seggregatedObjectivesValues,
      (seggregatedObjectivesValue, index) => {
        const { label = "", children = [] } = seggregatedObjectivesValue || {};

        return isEvaluation ? (
          <React.Fragment key={index}>
            {label ? (
              <div className={classes.subjectLabel} style={subjectLabelStyle}>
                {label}
              </div>
            ) : null}
            {_.map(children, child => {
              return _.map(child, objectives => {
                const nonLeafObjective = _.first(
                  _.filter(objectives, objective => !objective.isLeaf)
                );

                const ibPypElementId = _.get(nonLeafObjective, "id", "");
                const selectedCriteria = _.find(ibPYPElementRatings, rating => {
                  return (
                    rating.ibPYPElementId === ibPypElementId &&
                    rating.academicCriteriaSet.id === criteriaSetId
                  );
                });

                const selectedCriterionComment = _.find(
                  ibPYPElementRemarks,
                  remark => {
                    return remark.ibPYPElementId === ibPypElementId;
                  }
                );

                const handleCriteriaClick = criteriaId => {
                  if (criteriaId == "null") {
                    deletePypElementRating({
                      studentIds,
                      studentId,
                      projectId,
                      parentId,
                      parentType,
                      type: "MYP_OBJECTIVES",
                      id: ibPypElementId,
                      setId: criteriaSetId,
                      ibPYPElementRatingsLocal,
                      setIBPYPElementRatingsLocal,
                      sourceFrom,
                      queryVars,
                      projectGroupType,
                    });
                  } else if (
                    _.get(selectedCriteria, "academicCriteriaValue.id", "") !=
                    criteriaId
                  ) {
                    createPypElementRating({
                      projectId,
                      parentId,
                      parentType,
                      studentId,
                      studentIds,
                      criteriaValue: criteriaId,
                      type: "MYP_OBJECTIVES",
                      id: ibPypElementId,
                      setId: criteriaSetId,
                      isPublished: isEvaluated,
                      criteriaSet,
                      academicCriteriaValue: _.find(academicCriteriaValues, {
                        id: criteriaId,
                      }),
                      academicTermId,
                      ibPYPElementRatingsLocal,
                      setIBPYPElementRatingsLocal,
                      sourceFrom,
                      queryVars,
                      projectGroupType,
                    });
                  }
                };

                const handleCriteriaUpdate = value => {
                  onUpdateCriteriaComment({
                    ibPYPElementId: ibPypElementId,
                    remarkMessage: value,
                  });
                };

                const ratingTextContainerClasses = classNames(
                  classes.ratingContainer,
                  {
                    "text-label-2": !isDSV2,
                    "text-label": isDSV2,
                  }
                );

                const tooltip = (
                  <div
                    style={styles.tooltipContainerStyle}
                    dangerouslySetInnerHTML={{
                      __html: t(
                        userType === "staff"
                          ? "classRoom:rubric_rating_tooltip"
                          : "classRoom:rubric_rating_tooltip_for_reader"
                      ),
                    }}
                  />
                );

                const tableWrapperClasses = classNames({
                  [classes.tableWrapper]: true,
                  [classes.tableWrapperV2]: isDSV2,
                });

                return (
                  <React.Fragment>
                    <div
                      className={objectiveNameClass}
                      style={objectiveNameStyle}
                    >
                      {nonLeafObjective.label}
                    </div>
                    <div className={tableWrapperClasses}>
                      <table className={classes.innerContainer}>
                        {getHeaderRow()}
                        <tbody>
                          {_.map(objectives, objective => {
                            return getTableRow({
                              objective,
                              isEvaluation,
                            });
                          })}
                        </tbody>
                      </table>
                    </div>
                    {(isEvaluationEnable || isEvaluated) &&
                    (userType === "staff" || showRatingContainerInViewMode) ? (
                      <div
                        className={classNames(
                          classes.ratingWithCommentContainer,
                          {
                            [classes.ratingWithCommentContainerV2]: isDSV2,
                          }
                        )}
                      >
                        <div
                          className={ratingTextContainerClasses}
                          style={rubricRatingContainerStyle}
                        >
                          <div
                            className={classes.ratingTextContainer}
                            style={rubricRatingTextContainerStyle}
                          >
                            <div className={classes.finalRatingHeaderText}>
                              {t("unitPlan:best_fit_rating_for_text_normal", {
                                label: _.get(nonLeafObjective, "label", ""),
                              })}
                              {userType === "staff" ? (
                                <div className={classes.alertIconContainer}>
                                  <ConditionalWrapper
                                    type="wrapperAsElement"
                                    condition={true}
                                    wrapper={
                                      isDSV2 ? (
                                        <Tooltip
                                          tooltip={tooltip}
                                          placement="right"
                                        />
                                      ) : (
                                        <LinkWithTooltip
                                          tooltip={t(
                                            userType === "staff"
                                              ? "classRoom:rubric_rating_tooltip"
                                              : "classRoom:rubric_rating_tooltip_for_reader"
                                          )}
                                          href="#"
                                          tooltipContainerStyle={
                                            styles.tooltipContainerStyle
                                          }
                                          placement="right"
                                          trigger={["hover", "focus"]}
                                        />
                                      )
                                    }
                                  >
                                    <InformationOutlined
                                      size="xxx-small"
                                      variant="subtle"
                                      tabIndex={0}
                                      className={classes.alertIcon}
                                    />
                                  </ConditionalWrapper>
                                </div>
                              ) : null}
                            </div>
                          </div>
                          <div
                            className={classNames(
                              { [classes.ratingCompContainer]: true },
                              { [classes.disabled]: !isEvaluationEnable }
                            )}
                            style={rubricRatingCompContainerStyle}
                          >
                            <CriteriaRating
                              shouldUpdateOnValueChnage={true}
                              isEvaluation={isEvaluationEnable}
                              options={academicCriteriaValues}
                              value={_.get(
                                selectedCriteria,
                                "academicCriteriaValue.id",
                                ""
                              )}
                              isViewMode={showRatingContainerInViewMode}
                              onCriteriaClick={handleCriteriaClick}
                              timeOut={
                                parentType === "STUDENT_ASSIGNMENT" ? 0 : 1000
                              }
                              containerStyle={
                                criteriaRatingElementsContainerStyle
                              }
                              customRatingElementClass={
                                criteriaRatingCustomRatingElementClass
                              }
                              selectedCriteriaRef={
                                !_.includes(
                                  activeComment,
                                  "criterion_comment"
                                ) && _.includes(activeComment, ibPypElementId)
                                  ? commentInputRef
                                  : undefined
                              }
                              showEmptyValueInSelection={
                                showEmptyValueInSelection
                              }
                              isSelectedClickDisabled={isSelectedClickDisabled}
                              testId={`${TEST_ID_PREFIX}-${ibPypElementId}`}
                            />
                          </div>
                        </div>
                        {showStrandComment ? (
                          <CriteriaComment
                            key={projectId}
                            disabled={
                              !isEvaluationEnable ||
                              showRatingContainerInViewMode
                            }
                            criteriaRemarks={selectedCriterionComment}
                            updateComment={handleCriteriaUpdate}
                            inputRef={
                              _.includes(activeComment, "criterion_comment") &&
                              _.includes(activeComment, ibPypElementId)
                                ? commentInputRef
                                : undefined
                            }
                            testId={`${TEST_ID_PREFIX}-${ibPypElementId}`}
                          />
                        ) : null}
                      </div>
                    ) : null}
                  </React.Fragment>
                );
              });
            })}
          </React.Fragment>
        ) : (
          <tbody>
            {_.map(children, child => {
              _.map(child, objective => {
                return getTableRow({ objective, isEvaluation });
              });
            })}
          </tbody>
        );
      }
    );
  };

  const getTableRow = ({ objective }) => {
    const {
      achievementLevels,
      assessmentTool,
      isEvaluationEnable,
      updateEvaluationCell,
      toolEvaluationResponses,
      userType,
      isEvaluation,
      cellStyle = {},
    } = props;
    const { id, label, isLeaf } = objective;
    const { descriptors = [] } = assessmentTool;
    if (!isLeaf && isEvaluation) {
      return null;
    }

    const groupedToolEvaluationResponse = _.groupBy(
      toolEvaluationResponses,
      toolEvaluationResponse => toolEvaluationResponse.creatorType
    );

    const staffToolEvaluationResponses = _.get(
      groupedToolEvaluationResponse,
      "STAFF",
      []
    );

    const headerColumnCellClass = classNames(
      { "text-body-3": !isDSV2 },
      { "text-body-s": isDSV2 },
      { [classes.headerColumnCell]: true },
      { [classes.headerColumnCellV2]: isDSV2 },
      { [classes.mergedCell]: !isLeaf }
    );
    return (
      <tr key={id}>
        <td
          className={headerColumnCellClass}
          colSpan={isLeaf ? 1 : achievementLevels.length + 1}
        >
          {label}
        </td>
        {isLeaf &&
          _.map(achievementLevels, level => {
            const descriptor =
              _.find(descriptors, descriptor => {
                return (
                  descriptor.objective.id == objective.id &&
                  descriptor.achievementLevel.id == level.id
                );
              }) || {};

            const objectiveId = _.get(descriptor, "objective.id", "");
            const achievementLevelId = _.get(
              descriptor,
              "achievementLevel.id",
              ""
            );

            const updatedDescriptorLabel = getStyleStrippedText({
              text: descriptor.label,
            }).replace(/<br \/>/g, "<br>");

            const isSelected =
              _.findIndex(
                staffToolEvaluationResponses,
                response =>
                  response.rowId == objectiveId &&
                  response.columnId == achievementLevelId
              ) >= 0;

            const cellClass = classNames(
              { "text-body-3": !isDSV2 },
              { "text-body-s": isDSV2 },
              { [classes.disabled]: !isEvaluationEnable },
              { [classes.bodyRowCell]: true },
              { [classes.bodyRowCellV2]: isDSV2 },
              {
                [classes.clickableCell]:
                  isEvaluationEnable && userType == "staff",
              },
              {
                [classes.clickableCellV2]:
                  isEvaluationEnable && userType == "staff" && isDSV2,
              },
              {
                [classes.clickableStudentCell]:
                  isEvaluationEnable && userType == "student",
              },
              {
                [classes.selectableCell]: isSelected,
              },
              {
                [classes.selectableCellV2]: isSelected && isDSV2,
              }
            );

            const handleClick = () => {
              if (isEvaluationEnable) {
                if (descriptor?.objective?.id) {
                  updateEvaluationCell({
                    rowId: descriptor.objective.id,
                    columnId:
                      userType == "staff" && isSelected
                        ? null
                        : descriptor.achievementLevel.id,
                    isSelected,
                  });
                }
              }
            };

            return (
              <td
                key={level.id}
                className={cellClass}
                style={cellStyle}
                tabIndex={isEvaluationEnable ? 0 : -1}
                onClick={handleClick}
                onKeyDown={e => onEnterOrSpaceKeyDown(e, handleClick)}
                data-test-id={`${TEST_ID_PREFIX}-level-${objectiveId}-${achievementLevelId}`}
              >
                {isSelected ? (
                  <div className={classes.tickSvgContainer}>
                    {isSelected && (
                      <div className={classes.tickSvgComponent}>
                        <TickCircleFilled
                          className={classNames(classes.tickIcon, {
                            [classes.tickIconV2]: isDSV2,
                          })}
                          size={"xxx-small"}
                          variant={"link"}
                        />
                      </div>
                    )}
                  </div>
                ) : null}

                <div
                  dangerouslySetInnerHTML={{ __html: updatedDescriptorLabel }}
                ></div>
              </td>
            );
          })}
      </tr>
    );
  };

  return (
    <>
      <div
        className={classes.container}
        style={
          mode == "view"
            ? { marginTop: 0, ...rubricTableContainerStyle }
            : rubricTableContainerStyle
        }
      >
        {!isEvaluation ? (
          <div>
            <table className={classes.innerContainer}>
              {getHeaderRow()}
              {getTableBody()}
            </table>
          </div>
        ) : (
          getTableBody()
        )}
      </div>
      <hr
        className={classNames(classes.horizontalRule, {
          [classes.horizontalRuleV2]: isDSV2,
        })}
      />
    </>
  );
};

const mapActionCreators = {
  writeAssessmentToolElementLocal,
  updateField,
  getCriteriaSetOfPypType,
  createPypElementRating,
  deletePypElementRating,
  getProjectCriteriaGradeCriteriaSetWithCurriculum,
};

const _getSeggregatedObjectives = ({ isEvaluation, objectives }) => {
  if (isEvaluation) {
    const objectivesObj = _.keyBy(objectives, "id");
    const seggregatedObjectives = _.reduce(
      objectives,
      (result, currObj) => {
        if (!currObj.isLeaf) {
          const groupedObjectives = [];
          groupedObjectives.push(currObj);
          const { children } = currObj;
          _.forEach(children, ({ id: childrenId }) => {
            if (objectivesObj[childrenId]) {
              groupedObjectives.push(objectivesObj[childrenId]);
            }
          });
          result.push(groupedObjectives);
        }
        return result;
      },
      []
    );
    return seggregatedObjectives;
  }
  return [objectives];
};

const getSeggregatedObjectives = _.memoize(
  params => _getSeggregatedObjectives(params),
  params => JSON.stringify(params)
);

const _flattenParentChild = nodes => {
  const result = [];

  function flatten(nodes) {
    _.forEach(nodes, node => {
      if (node?.depth !== 1) result.push(node);
      flatten(node?.children);
    });
  }

  flatten(nodes);
  return result;
};

const flattenParentChild = _.memoize(
  params => _flattenParentChild(params),
  params => JSON.stringify(params)
);

const mapStateToProps = (state, ownProps) => {
  const { nestedObjectives = [], isEvaluation } = ownProps;

  /*
  nestedObjectives = [
    {
      label:"English",
      children:[
        {
          Label:"Listening",
          children: [...]
        }
      ]
    }
  ]

  flattenNodes = [
    {
      Label: "English",
    },
    {
      Label: "Listening"
    }
  ]

  seggregatedObjectives = [
    {
      label:"English",
      children: [
        [[...],[...],[...]],
        [[...],[...]]
      ]
    }
  ]
  */
  const seggregatedObjectives = {};
  _.forEach(nestedObjectives, val => {
    const flattenNodes = flattenParentChild([val]);

    // Zero depth node contains the subject label
    let zeroDepthNode = {};
    const remainingNodes = [];
    _.forEach(flattenNodes, node => {
      if (node?.depth === 0) {
        zeroDepthNode = node;
      } else {
        remainingNodes.push(node);
      }
    });

    const remainingNodesChildren = [];
    _.forEach(remainingNodes, node => {
      if (!seggregatedObjectives[zeroDepthNode?.id]) {
        seggregatedObjectives[zeroDepthNode?.id] = {
          id: zeroDepthNode?.id,
          label: zeroDepthNode?.label,
          children: [],
        };
      }

      remainingNodesChildren.push(node);
    });

    seggregatedObjectives[zeroDepthNode?.id]?.children?.push(
      getSeggregatedObjectives({
        objectives: remainingNodesChildren,
        isEvaluation,
      })
    );
  });

  return {
    userType: getUserType(),
    seggregatedObjectives,
  };
};

export default compose(connect(mapStateToProps, mapActionCreators))(
  RubricTableWithComment
);
